/**
 * iter-228 — AgentMessage
 *
 * Renders a single agent chat bubble. For assistant messages the text
 * is rendered as markdown (headings, bullet lists, tables, inline
 * code, bold/italic) instead of the raw code-looking string the user
 * complained about. Each assistant reply also gets a small "expand"
 * icon that opens a full-screen modal so long / dense answers can be
 * read comfortably.
 */
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
// iter-228 — enable GFM so pipe-style tables, strikethrough, task-lists,
// and autolinks render properly instead of appearing as raw source.
import remarkGfm from 'remark-gfm';
import { Maximize2, X } from 'lucide-react';

// Shared markdown-component overrides so both the inline bubble and
// the fullscreen modal render tables, code, and lists consistently.
const mdComponents = {
  h1: (p) => <h1 className="text-lg font-bold mt-3 mb-2" {...p} />,
  h2: (p) => <h2 className="text-base font-bold mt-3 mb-1.5" {...p} />,
  h3: (p) => <h3 className="text-sm font-semibold mt-2 mb-1" {...p} />,
  h4: (p) => <h4 className="text-[13px] font-semibold mt-2 mb-1" {...p} />,
  p:  (p) => <p className="mb-2 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc ml-4 mb-2 space-y-0.5" {...p} />,
  ol: (p) => <ol className="list-decimal ml-4 mb-2 space-y-0.5" {...p} />,
  li: (p) => <li className="leading-snug" {...p} />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  code: ({ inline, ...rest }) => inline
    ? <code className="px-1 py-0.5 rounded bg-violet-100 text-violet-800 font-mono text-[10.5px]" {...rest} />
    : <code className="block p-2 rounded bg-slate-900 text-slate-100 font-mono text-[10.5px] overflow-x-auto whitespace-pre" {...rest} />,
  pre: (p) => <pre className="mb-2" {...p} />,
  table: (p) => (
    <div className="overflow-x-auto my-2 rounded border border-slate-200">
      <table className="w-full text-[11px] border-collapse" {...p} />
    </div>
  ),
  thead: (p) => <thead className="bg-slate-50" {...p} />,
  th: (p) => <th className="px-2 py-1 text-left font-semibold border-b border-slate-200 text-slate-700" {...p} />,
  td: (p) => <td className="px-2 py-1 border-b border-slate-100 align-top" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-violet-300 pl-2 my-2 text-slate-700 italic" {...p} />,
  hr: () => <hr className="my-3 border-slate-200" />,
  a:  (p) => <a className="text-violet-700 underline" target="_blank" rel="noreferrer" {...p} />,
};

export function AgentMessage({ role, text, fallback }) {
  const [full, setFull] = useState(false);

  if (role === 'user') {
    return (
      <div className="mb-2 flex justify-end">
        <div className="px-2.5 py-1.5 rounded-lg text-[11.5px] max-w-[88%] whitespace-pre-wrap bg-violet-600 text-white">
          {text}
        </div>
      </div>
    );
  }

  const bubbleClass = fallback
    ? 'bg-amber-50 text-amber-900 border border-amber-200'
    : 'bg-white text-gray-800 border border-violet-100';

  return (
    <>
      <div className="mb-2 flex justify-start">
        <div className={`relative pl-2.5 pr-7 py-1.5 rounded-lg text-[11.5px] max-w-[88%] ${bubbleClass}`}
              data-testid="agent-message-assistant">
          <div className="prose prose-sm max-w-none text-[11.5px] leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{text}</ReactMarkdown>
          </div>
          {/* Expand-to-fullscreen button. Only shown for assistant
              replies since users rarely need to read their own prompt
              full-screen. */}
          <button
            type="button"
            onClick={() => setFull(true)}
            className="absolute top-1 right-1 opacity-40 hover:opacity-100 transition-opacity text-slate-500"
            title="Expand response"
            data-testid="agent-message-expand"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {full && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setFull(false)}
          data-testid="agent-message-fullscreen"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
              <div className="text-xs font-semibold text-slate-700">Agent response</div>
              <button
                onClick={() => setFull(false)}
                className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-600"
                data-testid="agent-message-fullscreen-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none text-[13px] leading-relaxed text-slate-800">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{text}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AgentMessage;
